/** @prettier */

import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  Typography,
  Stack,
  Slider,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
  Fade,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

//MTH component import
import TextFieldSelectBox from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {
  getCompositionsSitesBySiteID,
  getCompositionsGroups,
  putSettingLogic,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {apiErrorAlert} from '../../functions/apiErrorAlert';
import {
  pullDownMenu,
  logic,
  sliderMarks,
  iconWhite,
  iconBlack,
} from '../../define';
import convertLogic from '../../functions/convertLogic';

/**
 * ロジック 編集ダイアログ
 * @param props props
 * - logicInfo ロジック設定情報
 * - targetLogic 編集するロジック情報
 * - isOpenDialog ダイアログ表示フラグ
 * - setIsOpenDialog ダイアログ表示フラグ 設定関数
 * - setLogicInfoChange ロジック設定更新フラグ 設定関数
 * @returns JSX
 */
const LogicSettingDialog = (props: {
  logicInfo: apiIF.res.getSettingLogic;
  targetLogic: apiIF.res.settingLogic | null;
  isOpenDialog: boolean;
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setLogicInfoChange: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const siteID = props.logicInfo['site-id'];

  //prettier-ignore
  const [template, setTemplate] = useState<'nop' | 'warm-up-control' | 'cool-down-control' | 'high-temperature-alert'>('nop'); //Template設定値
  const [action, setAction] = useState<'control' | 'notification'>('control'); //動作種別
  //ロジック設定値
  const [control, setControl] = useState<apiIF.res.settingLogic['control']>(''); //有効無効
  const [scope, setScope] = useState<apiIF.res.settingLogic['scope']>(''); //制御対象範囲
  //prettier-ignore
  const [targetID, setTargetID] = useState<apiIF.res.settingLogic['target-id']>(''); //制御対象ID
  const [trigger, setTrigger] = useState<apiIF.res.settingLogic['trigger']>(''); //制御トリガー
  //prettier-ignore
  const [threshold, setThreshold] = useState<apiIF.res.settingLogic['threshold']>(''); //トリガー閾値
  const [range, setRange] = useState<apiIF.res.settingLogic['range']>(''); //トリガー範囲
  const [runStop, setRunStop] = useState<apiIF.res.command['run-stop']>(''); //運転/停止
  //prettier-ignore
  const [operationMode, setOperationMode] = useState<apiIF.res.command['operation-mode']>(''); //運転モード
  //prettier-ignore
  const [presetTemp, setPresetTemp] = useState<apiIF.res.command['preset-temperature']>(''); //設定温度
  const [airFlow, setAirFlow] = useState<apiIF.res.command['air-flow']>(''); //風量
  //prettier-ignore
  const [notifyAuthority, setNotifyAuthority] = useState<apiIF.res.notice['notify-authority']>([]); //通知するユーザ権限
  //設定温度未設定判定
  const [enabledTempSlider, setEnabledTempSlider] = useState(
    (() => {
      if (
        props.targetLogic !== null &&
        'preset-temperature' in props.targetLogic['action'] &&
        props.targetLogic['action']['preset-temperature'] === ''
      ) {
        return false;
      }
      return true;
    })()
  );
  const [scopeChanged, setScopeChanged] = useState(false); //制御対象範囲の変更フラグ
  //prettier-ignore
  const [siteInfo, setSiteInfo] = useState<apiIF.res.getCompositionsSitesBySiteID | null>(null); //サイト情報
  //prettier-ignore
  const [groupInfo, setGroupInfo] = useState<apiIF.res.getCompositionsGroups | null>(null); //グループ情報
  const [getAPIWait, setGetAPIWait] = useState(false); //サイト及びグループ情報取得API応答待ちフラグ
  const [editAPIWait, setEditAPIWait] = useState(false); //ロジック設定修正API応答待ちフラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ

  //サイト及びグループ情報を取得
  useEffect(() => {
    //ダイアログを開いた時のみAPIを実行
    if (props.isOpenDialog === false) return;

    setGetAPIWait(true);

    const promises = [];
    //サイト情報取得
    promises.push(getCompositionsSitesBySiteID(siteID));
    //グループ情報取得
    const parameter: apiIF.reqParameters.getCompositionsGroups = {
      'fields': 'group-name,unit-system,unit-informations',
      'max-result': 400,
    };
    promises.push(getCompositionsGroups(siteID, parameter));
    Promise.all(promises)
      .then(result => {
        const siteInfo = result[0] as apiIF.res.getCompositionsSitesBySiteID;
        const groupInfo = result[1] as apiIF.res.getCompositionsGroups;

        setSiteInfo(siteInfo);
        setGroupInfo(groupInfo);
      })
      .finally(() => {
        setGetAPIWait(false);
      });
  }, [props.isOpenDialog, siteID]);

  //設定値自動入力
  useEffect(() => {
    //選択されたTemplateに基づき各値を自動入力
    switch (template) {
      case 'nop':
        //未選択
        break;
      case 'warm-up-control':
        /**
         * 冷えすぎ防止機能
         * 対象：空調機
         * トリガー：吸込温度
         * 閾値：5度以下
         * 動作：制御
         * 指令値：運転、暖房、16度、風量自動
         */
        setScope('sl');
        setTrigger('indoor-temperature');
        setThreshold('5');
        setRange('or-less');
        setAction('control');
        setRunStop('run');
        setOperationMode('heating');
        setEnabledTempSlider(true);
        setPresetTemp('16.0');
        setAirFlow('auto');
        setNotifyAuthority([]);
        break;
      case 'cool-down-control':
        /**
         * 暑すぎ防止機能
         * 対象：空調機
         * トリガー：吸込温度
         * 閾値：30度以上
         * 動作：制御
         * 指令値：運転、冷房、26度、風量自動
         */
        setScope('sl');
        setTrigger('indoor-temperature');
        setThreshold('30');
        setRange('or-more');
        setAction('control');
        setRunStop('run');
        setOperationMode('cooling');
        setEnabledTempSlider(true);
        setPresetTemp('26.0');
        setAirFlow('auto');
        setNotifyAuthority([]);
        break;
      case 'high-temperature-alert':
        /**
         * 高温通知機能
         * 対象：空調機
         * トリガー：吸込温度
         * 閾値：30度以上
         * 動作：通知
         */
        setScope('sl');
        setTrigger('indoor-temperature');
        setThreshold('30');
        setRange('or-more');
        setAction('notification');
        setRunStop('');
        setOperationMode('');
        setPresetTemp('');
        setEnabledTempSlider(false);
        setAirFlow('');
        setNotifyAuthority(['admin']);
        break;
    }
  }, [template]);

  //ロジックの各設定値を取得
  useEffect(() => {
    if (props.targetLogic === null) return;

    if (
      props.targetLogic['control'] === 'active' ||
      props.targetLogic['control'] === 'inactive'
    ) {
      setControl(props.targetLogic['control']);
      setScope(props.targetLogic['scope']);
      setTargetID(props.targetLogic['target-id']);
      setTrigger(props.targetLogic['trigger']);
      setThreshold(props.targetLogic['threshold']);
      setRange(props.targetLogic['range']);
      if ('preset-temperature' in props.targetLogic['action']) {
        setRunStop(props.targetLogic['action']['run-stop']);
      }
      if ('operation-mode' in props.targetLogic['action']) {
        setOperationMode(props.targetLogic['action']['operation-mode']);
      }
      if ('preset-temperature' in props.targetLogic['action']) {
        setPresetTemp(props.targetLogic['action']['preset-temperature']);
      }
      if ('air-flow' in props.targetLogic['action']) {
        setAirFlow(props.targetLogic['action']['air-flow']);
      }
      //動作内容により動作種別を判定
      if ('notify-authority' in props.targetLogic['action']) {
        setNotifyAuthority(props.targetLogic['action']['notify-authority']);
        setAction('notification');
      } else {
        setAction('control');
      }
    } else {
      //未設定のロジックである場合、必須項目に初期値を設定する
      setTemplate('nop');
      setControl('active');
      setScope('site');
      setTrigger('indoor-temperature');
      setThreshold('30');
      setRange('or-more');
      setAction('control');
      setRunStop('');
      setOperationMode('');
      setPresetTemp('');
      setAirFlow('');
      setEnabledTempSlider(false);
      setNotifyAuthority([]);
    }
  }, [props.targetLogic, props.isOpenDialog]);

  //制御対象IDを空にする
  useEffect(() => {
    //対象範囲のテキストボックスが操作されていない場合
    if (scopeChanged === false) return;

    setTargetID('');
    setScopeChanged(false);
  }, [scope, scopeChanged]);

  //動作種別変更時に項目に初期値を設定する
  useEffect(() => {
    if (action === 'control') {
      setNotifyAuthority([]);
    } else {
      setRunStop('');
      setOperationMode('');
      setPresetTemp('');
      setAirFlow('');
      setEnabledTempSlider(false);
      setNotifyAuthority(['admin']);
    }
  }, [action]);

  //暖房モード以外に切り替え時に設定温度が暖房モードでのみ設定可能な値になっている場合、設定温度を変更する
  useEffect(() => {
    if (
      operationMode === 'heating' ||
      presetTemp === '' ||
      Number(presetTemp) >= logic.SET_TEMP_MIN
    ) {
      return;
    } else {
      setPresetTemp(String(logic.SET_TEMP_MIN.toFixed(1)));
    }
  }, [operationMode, presetTemp]);

  /**
   * ロジック対象のテキストボックスに表示するラベル文字の選択
   * @param  {apiIF.res.settingLogic['scope']} scope  ロジック対象
   * @returns ロジック対象のラベル
   */
  const selectLabel = (scope: apiIF.res.settingLogic['scope']) => {
    switch (scope) {
      case '': //fall through
      case 'site':
        return ' ';
      case 'floor':
        return trans('LogicSettingDialog.textFieldLabel.floor');
      case 'group':
        return trans('LogicSettingDialog.textFieldLabel.group');
      case 'sl':
        return trans('LogicSettingDialog.textFieldLabel.unit');
    }
  };

  /**
   * 有効な入力状態であるか確認
   * @returns boolean
   */
  const isValidInput = () => {
    /** 動作内容一覧 */
    const actionValues = [
      runStop,
      operationMode,
      presetTemp,
      airFlow,
      notifyAuthority,
    ];

    //動作内容が一つも設定されていない場合
    if (actionValues.every(value => value === '' || value.length === 0)) {
      return false;
    }
    //制御対象範囲がフロア、グループまたはslの時に、制御対象が設定されていない場合
    if (scope === 'floor' || scope === 'group' || scope === 'sl') {
      if (targetID === '') {
        return false;
      }
    }
    return true;
  };

  /**
   * Target（範囲）変更時処理
   * @param scope 範囲
   */
  const handleTargetScope = (scope: apiIF.res.settingLogic['scope']) => {
    //テキストボックスで選択された値を設定
    setScope(scope);
    //テキストボックスが操作されたことを記憶
    setScopeChanged(true);
  };

  /**
   * 制御対象の選択肢作成
   * @param scope 制御範囲
   * @param siteInfo サイト情報
   * @param groupInfo グループ情報
   * @returns 制御対象の選択肢を含む配列
   */
  const createTargetChoices = (
    scope: apiIF.res.settingLogic['scope'],
    siteInfo: apiIF.res.getCompositionsSitesBySiteID | null,
    groupInfo: apiIF.res.getCompositionsGroups | null
  ) => {
    const result: {value: string; label: string}[] = [{value: '', label: '-'}];
    //制御範囲に基づき選択肢を作成
    switch (scope) {
      case 'floor':
        if (siteInfo === null) break;
        //サイトの階数分繰り返し処理
        for (
          let floor = Number(siteInfo['above-ground']);
          floor >= Number(siteInfo['under-ground']);
          floor--
        ) {
          /** 翻訳後のフロア名 */
          let floorName = '';
          if (floor > 0) {
            floorName = String(floor);
          } else if (floor === 0) {
            //prettier-ignore
            floorName = trans('LogicSettingDialog.textFieldContent.groundFloor');
          } else if (floor >= -4) {
            //prettier-ignore
            floorName = trans(`LogicSettingDialog.textFieldContent.basement${String(floor).replace('-', '')}`);
          }
          result.push({value: String(floor), label: floorName});
        }
        break;
      case 'group':
        if (groupInfo === null) break;
        //SL設定のユニット登録用グループの情報を抽出
        groupInfo['group-informations'].forEach(group => {
          if (
            group['unit-system'] === 'SL' &&
            group['group-name'] !== undefined
          ) {
            result.push({value: group['group-id'], label: group['group-name']});
          }
        });
        break;
      case 'sl':
        if (groupInfo === null) break;
        //SL設定のユニット登録用グループの情報を抽出
        groupInfo['group-informations'].forEach(group => {
          if (
            group['unit-system'] === 'SL' &&
            group['unit-informations'] !== undefined
          ) {
            group['unit-informations'].forEach(unit => {
              result.push({value: unit['unit-id'], label: unit['unit-name']});
            });
          }
        });
        break;
      default:
        break;
    }
    return result;
  };

  /**
   * OKボタン押下時処理
   */
  const handleOK = () => {
    setEditAPIWait(true);
    //APIに渡すデータ作成
    const editData: apiIF.reqParameters.putSettingLogic = {
      //現在のロジック設定情報を基に作成する
      settings: props.logicInfo['settings'].map(logic => {
        let tempLogic: apiIF.res.settingLogic;
        if (
          props.targetLogic !== null &&
          logic['priority-level'] === props.targetLogic['priority-level']
        ) {
          //編集対象の場合
          tempLogic = {
            'priority-level': logic['priority-level'],
            'control': control,
            'scope': scope,
            'target-id': targetID,
            'target-name': logic['target-name'],
            'trigger': trigger,
            'threshold': threshold,
            'range': range,
            'action': {
              'run-stop': runStop,
              'operation-mode': operationMode,
              'preset-temperature': presetTemp,
              'air-flow': airFlow,
              'notify-authority': notifyAuthority,
            },
          };
        } else {
          tempLogic = logic;
        }
        //ロジック設定をロジック編集APIに渡す形に変換する
        const editLogic = convertLogic(tempLogic);
        return editLogic;
      }),
    };
    //ロジック編集API
    putSettingLogic(siteID, editData)
      .then(response => {
        //ロジック設定更新フラグtrue
        props.setLogicInfoChange(true);
        handleClose();
      })
      .catch(err => {
        apiErrorAlert(trans);
      })
      .finally(() => {
        setEditAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
  };

  /**
   * Parameter（閾値）変更時処理
   * @param {Event} event スライダーの移動
   * @param {number | number[]} newValue 設定値
   * @param range トリガー範囲
   */
  const handleParameterThreshold = (
    event: Event,
    newValue: number | number[]
  ) => {
    setThreshold(String(newValue));
  };

  /**
   * Control（運転/停止）変更時処理
   * @param {React.MouseEvent<HTMLElement>} event マウスイベント
   * @param {string | null} newAlignment ボタンが押された運転/停止
   */
  const handleCommandPower = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    switch (newAlignment) {
      case 'run': //fall through
      case 'stop':
        setRunStop(newAlignment);
        break;
      default:
        //規定の運転停止以外の場合は未設定
        setRunStop('');
    }
  };

  /**
   * Control（モード）変更時処理
   * @param {React.MouseEvent<HTMLElement>} event マウスイベント
   * @param {string | null} newAlignment ボタンが押された運転モード
   */
  const handleCommandMode = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    switch (newAlignment) {
      case 'auto': //fall through
      case 'cooling': //fall through
      case 'heating': //fall through
      case 'fan': //fall through
      case 'dehumidify':
        setOperationMode(newAlignment);
        break;
      default:
        //規定のモード以外の場合は未設定
        setOperationMode('');
    }
  };

  /**
   * Control（風量）変更時処理
   * @param {React.MouseEvent<HTMLElement>} event マウスイベント
   * @param {string | null} newAlignment ボタンが押された風量
   */
  const handleCommandAirFlow = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    switch (newAlignment) {
      case 'auto': //fall through
      case 'low': //fall through
      case 'middle': //fall through
      case 'high': //fall through
      case 'powerful':
        setAirFlow(newAlignment);
        break;
      default:
        //規定のモード以外の場合は未設定
        setAirFlow('');
    }
  };

  /**
   * ActionがControl時の表示
   * @param {React.MouseEvent<HTMLElement>} event マウスイベント
   * @param {string | null} newAlignment ボタンが押された風量
   */
  const ActionContents = () => {
    /** スライダー値保存用変数 */
    const [sliderTemp, setSliderTemp] = useState<string>(presetTemp); //設定温度
    /**
     * Parameter（設定温度スライダー）有効無効変更時処理
     * @param {Event} event チェックボタンON/OFF
     */
    const handleNoSetTemperature = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setEnabledTempSlider(event.target.checked);

      if (event.target.checked && operationMode === 'heating') {
        setPresetTemp(logic.SET_TEMP_HEATING_MIN.toFixed(1));
        setSliderTemp(logic.SET_TEMP_HEATING_MIN.toFixed(1));
      } else if (event.target.checked) {
        setPresetTemp(logic.SET_TEMP_MIN.toFixed(1));
        setSliderTemp(logic.SET_TEMP_MIN.toFixed(1));
      } else {
        setPresetTemp('');
        setSliderTemp('');
      }
    };

    /**
     * Parameter（設定温度）スライダードラッグ時処理
     * @param {Event} event スライダーのドラッグ
     * @param {number | number[]} newValue 設定値
     */
    const handlePresetTemperatureChange = (
      event: Event,
      newValue: number | number[]
    ) => {
      setSliderTemp(String((newValue as number).toFixed(1)));
    };

    /**
     * Parameter（設定温度）スライダードロップ時処理
     * @param {Event | React.SyntheticEvent<Element, Event>} event スライダーのドロップ
     * @param {number | number[]} newValue 設定値
     */
    const handlePresetTemperatureChangeCommitted = (
      event: Event | React.SyntheticEvent<Element, Event>,
      newValue: number | number[]
    ) => {
      setPresetTemp(String((newValue as number).toFixed(1)));
    };

    return (
      <Stack className={'logic__dialog__contents__controlGroup'} spacing={2}>
        {/* 運転/停止 */}
        <Box>
          <Typography>
            {trans('LogicSettingDialog.actionContents.power')}
          </Typography>
          <ToggleButtonGroup
            value={runStop}
            exclusive
            onChange={handleCommandPower}
            aria-label="Run stop change"
            className="logic__dialog__contents__controlGroup__edit__runStop__toggleButton">
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.AC_ON.alt}`
              )}`}
              value="run"
              aria-label="on"
              className={
                runStop === 'run'
                  ? 'logic__dialog__contents__controlGroup__edit__runStop__toggleButton--selected'
                  : ''
              }>
              <img
                id="mv-btnSelectRunStopRun"
                className="logic__dialog__contents__controlGroup__edit__runStop__toggleButton__image"
                src={
                  runStop === 'run' ? iconWhite.AC_ON.src : iconBlack.AC_ON.src
                }
                alt={
                  runStop === 'run'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.AC_ON.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.AC_ON.alt}`
                      )}`
                }
              />
            </ToggleButton>
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.AC_OFF.alt}`
              )}`}
              value="stop"
              aria-label="off"
              className={
                runStop === 'stop'
                  ? 'logic__dialog__contents__controlGroup__edit__runStop__toggleButton--selected'
                  : ''
              }>
              <img
                id="mv-btnSelectRunStopStop"
                className="logic__dialog__contents__controlGroup__edit__runStop__toggleButton__image"
                src={
                  runStop === 'stop'
                    ? iconWhite.AC_OFF.src
                    : iconBlack.AC_OFF.src
                }
                alt={
                  runStop === 'stop'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.AC_OFF.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.AC_OFF.alt}`
                      )}`
                }
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {/* 運転モード */}
        <Box>
          <Typography>
            {trans('LogicSettingDialog.actionContents.mode')}
          </Typography>
          <ToggleButtonGroup
            value={operationMode}
            exclusive
            onChange={handleCommandMode}
            aria-label="Operation mode change"
            className="logic__dialog__contents__controlGroup__edit__mode__toggleButton">
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.AUTO.alt}`
              )}`}
              value="auto"
              aria-label="auto"
              className={
                operationMode === 'auto'
                  ? 'logic__dialog__contents__controlGroup__edit__mode__toggleButton--selectedAuto'
                  : ''
              }>
              <img
                id="mv-btnSelectOperationModeAuto"
                className="logic__dialog__contents__controlGroup__edit__mode__toggleButton__image"
                src={
                  operationMode === 'auto'
                    ? iconWhite.AUTO.src
                    : iconBlack.AUTO.src
                }
                alt={
                  operationMode === 'auto'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.AUTO.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.AUTO.alt}`
                      )}`
                }
              />
            </ToggleButton>
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.COOLING.alt}`
              )}`}
              value="cooling"
              aria-label="cooling"
              className={
                operationMode === 'cooling'
                  ? 'logic__dialog__contents__controlGroup__edit__mode__toggleButton--selectedCooling'
                  : ''
              }>
              <img
                id="mv-btnSelectOperationModeCooling"
                className="logic__dialog__contents__controlGroup__edit__mode__toggleButton__image"
                src={
                  operationMode === 'cooling'
                    ? iconWhite.COOLING.src
                    : iconBlack.COOLING.src
                }
                alt={
                  operationMode === 'cooling'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.COOLING.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.COOLING.alt}`
                      )}`
                }
              />
            </ToggleButton>
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.HEATING.alt}`
              )}`}
              value="heating"
              aria-label="heating"
              className={
                operationMode === 'heating'
                  ? 'logic__dialog__contents__controlGroup__edit__mode__toggleButton--selectedHeating'
                  : ''
              }>
              <img
                id="mv-btnSelectOperationModeHeating"
                className="logic__dialog__contents__controlGroup__edit__mode__toggleButton__image"
                src={
                  operationMode === 'heating'
                    ? iconWhite.HEATING.src
                    : iconBlack.HEATING.src
                }
                alt={
                  operationMode === 'heating'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.HEATING.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.HEATING.alt}`
                      )}`
                }
              />
            </ToggleButton>
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.BLOWER.alt}`
              )}`}
              value="fan"
              aria-label="fan"
              className={
                operationMode === 'fan'
                  ? 'logic__dialog__contents__controlGroup__edit__mode__toggleButton--selectedFan'
                  : ''
              }>
              <img
                id="mv-btnSelectOperationModeFan"
                className="logic__dialog__contents__controlGroup__edit__mode__toggleButton__image"
                src={
                  operationMode === 'fan'
                    ? iconWhite.BLOWER.src
                    : iconBlack.BLOWER.src
                }
                alt={
                  operationMode === 'fan'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.BLOWER.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.BLOWER.alt}`
                      )}`
                }
              />
            </ToggleButton>
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.DRYING.alt}`
              )}`}
              value="dehumidify"
              aria-label="dehumidify"
              className={
                operationMode === 'dehumidify'
                  ? 'logic__dialog__contents__controlGroup__edit__mode__toggleButton--selectedDry'
                  : ''
              }>
              <img
                id="mv-btnSelectOperationModeDry"
                className="logic__dialog__contents__controlGroup__edit__mode__toggleButton__image"
                src={
                  operationMode === 'dehumidify'
                    ? iconWhite.DRYING.src
                    : iconBlack.DRYING.src
                }
                alt={
                  operationMode === 'dehumidify'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.DRYING.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.DRYING.alt}`
                      )}`
                }
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {/* 風量 */}
        <Box>
          <Typography>
            {trans('LogicSettingDialog.actionContents.fanSpeed')}
          </Typography>
          <ToggleButtonGroup
            value={airFlow}
            exclusive
            onChange={handleCommandAirFlow}
            aria-label="Fan speed change"
            id="mv-btnSelectOperationAirFlow"
            className="logic__dialog__contents__controlGroup__edit__fan__toggleButton">
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_AUTO.alt}`
              )}`}
              value="auto"
              aria-label="auto"
              id="mv-btnSelectOperationAirFlowAuto"
              className={
                airFlow === 'auto'
                  ? 'logic__dialog__contents__controlGroup__edit__fan__toggleButton--selected'
                  : ''
              }>
              <img
                className="logic__dialog__contents__controlGroup__edit__fan__toggleButton__image"
                src={
                  airFlow === 'auto'
                    ? iconWhite.FAN_SPEED_AUTO.src
                    : iconBlack.FAN_SPEED_AUTO.src
                }
                alt={
                  airFlow === 'auto'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_AUTO.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.FAN_SPEED_AUTO.alt}`
                      )}`
                }
              />
            </ToggleButton>
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_1.alt}`
              )}`}
              value="low"
              aria-label="low"
              id="mv-btnSelectOperationAirFlowLow"
              className={
                airFlow === 'low'
                  ? 'logic__dialog__contents__controlGroup__edit__fan__toggleButton--selected'
                  : ''
              }>
              <img
                className="logic__dialog__contents__controlGroup__edit__fan__toggleButton__image"
                src={
                  airFlow === 'low'
                    ? iconWhite.FAN_SPEED_1.src
                    : iconBlack.FAN_SPEED_1.src
                }
                alt={
                  airFlow === 'low'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_1.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.FAN_SPEED_1.alt}`
                      )}`
                }
              />
            </ToggleButton>
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_2.alt}`
              )}`}
              value="middle"
              aria-label="Medium"
              id="mv-btnSelectOperationAirFlowMiddle"
              className={
                airFlow === 'middle'
                  ? 'logic__dialog__contents__controlGroup__edit__fan__toggleButton--selected'
                  : ''
              }>
              <img
                className="logic__dialog__contents__controlGroup__edit__fan__toggleButton__image"
                src={
                  airFlow === 'middle'
                    ? iconWhite.FAN_SPEED_2.src
                    : iconBlack.FAN_SPEED_2.src
                }
                alt={
                  airFlow === 'middle'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_2.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.FAN_SPEED_2.alt}`
                      )}`
                }
              />
            </ToggleButton>
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_3.alt}`
              )}`}
              value="high"
              aria-label="high"
              id="mv-btnSelectOperationAirFlowHigh"
              className={
                airFlow === 'high'
                  ? 'logic__dialog__contents__controlGroup__edit__fan__toggleButton--selected'
                  : ''
              }>
              <img
                className="logic__dialog__contents__controlGroup__edit__fan__toggleButton__image"
                src={
                  airFlow === 'high'
                    ? iconWhite.FAN_SPEED_3.src
                    : iconBlack.FAN_SPEED_3.src
                }
                alt={
                  airFlow === 'high'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_3.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.FAN_SPEED_3.alt}`
                      )}`
                }
              />
            </ToggleButton>
            <ToggleButton
              title={`${trans(
                `LogicSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_4.alt}`
              )}`}
              value="powerful"
              aria-label="powerful"
              id="mv-btnSelectOperationAirFlowPowerful"
              className={
                airFlow === 'powerful'
                  ? 'logic__dialog__contents__controlGroup__edit__fan__toggleButton--selected'
                  : ''
              }>
              <img
                className="logic__dialog__contents__controlGroup__edit__fan__toggleButton__image"
                src={
                  airFlow === 'powerful'
                    ? iconWhite.FAN_SPEED_4.src
                    : iconBlack.FAN_SPEED_4.src
                }
                alt={
                  airFlow === 'powerful'
                    ? `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconWhite.FAN_SPEED_4.alt}`
                      )}`
                    : `${trans(
                        `LogicSettingDialog.image.alternativeText.${iconBlack.FAN_SPEED_4.alt}`
                      )}`
                }
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {/* 設定温度 */}
        <Box>
          <Typography>
            {trans('LogicSettingDialog.actionContents.setTemp')}
          </Typography>
          {/* 温度設定なし */}
          <FormControlLabel
            control={
              <Switch
                checked={enabledTempSlider}
                onChange={handleNoSetTemperature}
              />
            }
            label=""
          />
          {/* 暖房時  ：16°C～30°C（0.5°C刻み） */}
          {/* 暖房以外：18°C～30°C（0.5°C刻み） */}
          <Box className="logic__dialog__contents__controlGroup__setTemp">
            <Fade in={enabledTempSlider} appear={false}>
              <Slider
                aria-label="Set temperature"
                value={Number(sliderTemp)}
                valueLabelDisplay="on"
                valueLabelFormat={Number(sliderTemp).toFixed(1)}
                marks={
                  operationMode === 'heating'
                    ? sliderMarks.LOGIC_HEATING_SET_TEMP
                    : sliderMarks.LOGIC_SET_TEMP
                }
                min={
                  operationMode === 'heating'
                    ? logic.SET_TEMP_HEATING_MIN
                    : logic.SET_TEMP_MIN
                }
                max={logic.SET_TEMP_MAX}
                step={0.5}
                onChange={handlePresetTemperatureChange}
                onChangeCommitted={handlePresetTemperatureChangeCommitted}
                disableSwap
              />
            </Fade>
          </Box>
        </Box>
      </Stack>
    );
  };

  //対象のロジック設定情報を取得できない場合
  if (props.targetLogic === null) {
    return <></>;
  }
  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        aria-labelledby="form-dialog-title"
        className="logic__dialog"
        maxWidth="md">
        <DialogTitle id="form-dialog-title">
          {trans('LogicSettingDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Stack className="logic__dialog__contents">
            {/* テンプレート */}
            <TextFieldSelectBox
              label={trans('LogicSettingDialog.textFieldLabel.template')}
              choices={pullDownMenu.LOGIC_TEMPLATE}
              value={template}
              setValue={setTemplate}
              id={`logic${props.targetLogic['priority-level']}TemplateSelect`}
              margin="normal"
              class="logic__dialog__contents__templateSelectBox"
            />
            {/* 対象 */}
            <Stack direction="row" spacing={3}>
              {/* 範囲 */}
              <Box className="logic__dialog__contents__target">
                <TextFieldSelectBox
                  label={trans('LogicSettingDialog.textFieldLabel.target')}
                  choices={pullDownMenu.LOGIC_SCOPE}
                  value={scope}
                  setValue={handleTargetScope}
                  id={`logic${props.targetLogic['priority-level']}TargetScopeSelect`}
                  margin="normal"
                  class="logic__dialog__contents__selectBox"
                />
              </Box>
              {/* 対象 */}
              {/* サイト及びグループ情報を取得中は表示しない */}
              {getAPIWait ? (
                <Box>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Box
                  className={(() => {
                    if (scope === 'site') {
                      return 'logic__dialog__contents__target--site';
                    } else {
                      return 'logic__dialog__contents__target--noSite';
                    }
                  })()}>
                  <TextFieldSelectBox
                    label={selectLabel(scope)}
                    disabled={
                      scope !== 'floor' && scope !== 'group' && scope !== 'sl'
                    }
                    choices={createTargetChoices(scope, siteInfo, groupInfo)}
                    value={
                      scope !== 'floor' && scope !== 'group' && scope !== 'sl'
                        ? ''
                        : targetID
                    }
                    setValue={setTargetID}
                    id={`logic${props.targetLogic['priority-level']}TargetSelect`}
                    margin="normal"
                    placeholder="Please Select Logic Target."
                    class="logic__dialog__contents__selectBox"
                  />
                </Box>
              )}
            </Stack>
            {/* 状態値 */}
            <TextFieldSelectBox
              label={trans('LogicSettingDialog.textFieldLabel.condition')}
              choices={pullDownMenu.LOGIC_TRIGGER}
              value={trigger}
              setValue={setTrigger}
              id={`logic${props.targetLogic['priority-level']}ConditionSelect`}
              margin="normal"
              class="logic__dialog__contents__selectBox"
            />
            {/* Parameter */}
            {/* 閾値 */}
            <Stack
              direction="row"
              spacing={2}
              className="logic__dialog__contents__threshold">
              <Box className="logic__dialog__contents__threshold__slider">
                {/* -20°C～50°C（1°C刻み） */}
                <Slider
                  id={`logic${props.targetLogic['priority-level']}ParameterThresholdSlider`}
                  aria-label="Threshold"
                  //トリガー範囲に基づきスライダー範囲を切り替える
                  value={
                    range === 'or-more'
                      ? Number(threshold) //トリガー範囲：以上
                      : Number(threshold) //トリガー範囲：未満
                  }
                  track="normal"
                  valueLabelDisplay="on"
                  marks={sliderMarks.LOGIC_THRESHOLDS}
                  min={logic.THRESHOLD_MIN}
                  max={logic.THRESHOLD_MAX}
                  onChange={(e, v) => handleParameterThreshold(e, v)}
                  disableSwap
                />
              </Box>
              {/* 範囲 */}
              <TextFieldSelectBox
                choices={pullDownMenu.LOGIC_RANGE}
                value={range}
                setValue={setRange}
                id={`logic${props.targetLogic['priority-level']}ParameterRangeSelect`}
                margin="normal"
                class="logic__dialog__contents__selectBox"
              />
            </Stack>
            {/* 動作 */}
            <TextFieldSelectBox
              label={trans('LogicSettingDialog.textFieldLabel.action')}
              choices={pullDownMenu.LOGIC_ACTION}
              value={action}
              setValue={setAction}
              id={`logic${props.targetLogic['priority-level']}ActionSelect`}
              margin="normal"
              class="logic__dialog__contents__selectBox"
            />
            {/* Control */}
            {/* 動作種別が通知の場合は無効 */}
            {action === 'control' ? <ActionContents /> : <></>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={editAPIWait}
            onClick={handleClose}
            buttonText={trans('LogicSettingDialog.buttonText.cancel')}
            id={`mv-btnCancelEditLogic${props.targetLogic['priority-level']}`}
          />
          <PrimaryButton
            onClick={handleOK}
            buttonText={trans('LogicSettingDialog.buttonText.ok')}
            id={`mv-btnOkEditLogic${props.targetLogic['priority-level']}`}
            disabled={editAPIWait || isValidInput() === false}
            apiWait={editAPIWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`mv-logic${props.targetLogic['priority-level']}EditErrDlgBtn`}
      />
    </>
  );
};
export default LogicSettingDialog;
